import type { ChartLabelFormat, NormalizedBarChartData } from '../shared/types';

import { NULL_VALUE_LABEL } from '../shared/chart-tokens';
import { formatValue } from '../shared/format-value';

export const BAR_CHART_CATEGORY_KEY = 'category';

const SERIES_KEY_PREFIX = 'series-';

export const barChartSeriesKey = (seriesIndex: number): string =>
  `${SERIES_KEY_PREFIX}${seriesIndex}`;

export const barChartSeriesNullFlagKey = (seriesIndex: number): string =>
  `${barChartSeriesKey(seriesIndex)}-is-null`;

export interface BarChartRow {
  [BAR_CHART_CATEGORY_KEY]: string;
  [seriesSlug: string]: string | number | boolean | null;
}

/**
 * Recharts drops any row whose numeric dataKey resolves to `null` before its
 * `shape`/`LabelList` render props ever run. `toBarChartRows` substitutes `0` for
 * `null` under the numeric key so every row reaches those render props, and
 * carries the original null-ness under a companion flag key.
 *
 * This allows us to display a shape for null values.
 */
export const toBarChartRows = (
  normalized: NormalizedBarChartData
): BarChartRow[] =>
  normalized.categoryAxis.categories.map((category, categoryIndex) => {
    const row: BarChartRow = { [BAR_CHART_CATEGORY_KEY]: category };

    normalized.series.forEach((singleSeries, seriesIndex) => {
      const value = singleSeries.values[categoryIndex];
      const isNullValue = value === null;

      row[barChartSeriesKey(seriesIndex)] = isNullValue ? 0 : value;
      row[barChartSeriesNullFlagKey(seriesIndex)] = isNullValue;
    });

    return row;
  });

export const isBarChartRow = (value: unknown): value is BarChartRow =>
  typeof value === 'object' &&
  value !== null &&
  BAR_CHART_CATEGORY_KEY in value;

export interface BarValueSource {
  value: unknown;
  payload: unknown;
}

/**
 * Reconstructs the real `value: number | null` for a `<Bar shape={...}>`
 * callback from the sentinel row `toBarChartRows` produced, so
 * `BarChartBar`/`BarChartHorizontalBar` only ever see the real value.
 */
export const resolveSeriesValue = (
  source: BarValueSource,
  seriesIndex: number
): number | null => {
  const { value, payload } = source;

  if (
    isBarChartRow(payload) &&
    payload[barChartSeriesNullFlagKey(seriesIndex)] === true
  ) {
    return null;
  }

  if (typeof value === 'number') {
    return value;
  }

  return null;
};

/**
 * Derives the same display string `<LabelList>` and the sr-only `<Tooltip>`
 * both need. "N/A" for a null value, `formatValue` otherwise
 */
export const resolveSeriesLabel = (
  source: BarValueSource,
  seriesIndex: number,
  format: ChartLabelFormat
): string => {
  const value = resolveSeriesValue(source, seriesIndex);

  return value === null ? NULL_VALUE_LABEL : formatValue(value, format);
};

/**
 * The Tooltip is a single chart-wide element, not one per series, so it has no
 * `contextual closure to know which series a hovered point belongs to;
 * it recovers that from the Bar's own `dataKey` (e.g. `series-2`) instead.
 */
export const parseSeriesIndexFromDataKey = (
  dataKey: unknown
): number | null => {
  if (typeof dataKey !== 'string' || !dataKey.startsWith(SERIES_KEY_PREFIX)) {
    return null;
  }

  const seriesIndex = Number(dataKey.slice(SERIES_KEY_PREFIX.length));

  return Number.isInteger(seriesIndex) ? seriesIndex : null;
};

export const resolveTooltipSeriesLabel = (
  value: unknown,
  item: { dataKey?: unknown; payload?: unknown },
  format: ChartLabelFormat
): string => {
  const seriesIndex = parseSeriesIndexFromDataKey(item.dataKey);

  if (seriesIndex === null) {
    return NULL_VALUE_LABEL;
  }

  return resolveSeriesLabel(
    { value, payload: item.payload },
    seriesIndex,
    format
  );
};

export const buildBarChartAriaLabel = (
  normalized: NormalizedBarChartData,
  title?: string
): string => {
  const seriesCount = normalized.series.length;
  const categoryCount = normalized.categoryAxis.categories.length;
  const categoryNoun = categoryCount === 1 ? 'category' : 'categories';

  const subject = title ? `${title} bar chart` : 'Bar chart';
  const axisLabel = normalized.categoryAxis.label;
  const subjectWithAxis = axisLabel ? `${subject} by ${axisLabel}` : subject;

  return `${subjectWithAxis}, ${seriesCount} series across ${categoryCount} ${categoryNoun}`;
};
